import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { Navigate } from "react-router-dom";
import NotificationSlideOver from "src/containers/NotificationSlideOver";
import { AppSidebar } from "src/containers/menu/AppSidebar";
import UserNotificationList from "src/containers/user/UserNotificationList";
import Loading from "src/components/Loading";
import MigrationSchedulingModal from "src/components/MigrationSchedulingModal";
import TermsAndConditionsAgreement from "src/components/TermsAndConditionsAgreement";
import { isFeatureEnabled } from "src/helpers/utils";
import { useMigrationModal } from "src/hooks/useMigrationModal";
import useUser from "src/hooks/useUser";
import { useInvitationsByUser } from "src/hooks/useUserInvitation";
import MobileMenu from "../containers/MobileMenu";
import NetworkWarning from "../containers/inbox/NetworkWarning";
import useHilosStore from "../hooks/useHilosStore";
import { buildRoute } from "./router";

// Accounts excluded from the migration modal (e.g. TripleTen manages migration timing directly with us).
const MIGRATION_MODAL_EXCLUDED_ACCOUNT_IDS = new Set([1960, 1525]);

interface PrivateRouteProps {
  routeName?: string;
}

function NoInvitationsPage() {
  const { logout } = useUser();

  return (
    <div className="flex flex-1 w-full flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
      <div className="mx-auto w-full max-w-md">
        <div className="text-center">
          <Trans i18nKey="login:sign-up-restricted.title">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Invitation Required
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              You need an invitation to join a team. Please contact your team
              administrator for an invitation.
            </p>
            <p className="mt-4 text-sm text-gray-500">
              Once you receive an invitation, you'll be able to access your
              team's account.
            </p>
            <div className="mt-6">
              <button
                onClick={logout}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-hilos"
              >
                Sign Out
              </button>
            </div>
          </Trans>
        </div>
      </div>
    </div>
  );
}

function PrivateRoute({
  children,
  routeName,
}: PropsWithChildren<PrivateRouteProps>) {
  const { session } = useHilosStore();
  const invitations = useInvitationsByUser();
  const [showNotifications, setShowNotifications] = useState(false);
  const {
    isOpen: isMigrationModalOpen,
    handleOpenChange: handleMigrationModalOpenChange,
  } = useMigrationModal();
  const showMigrationModal = useMemo(
    () =>
      Boolean(session?.account) &&
      session?.role?.name === "Admin" &&
      !isFeatureEnabled({ key: "hide-migration", session }) &&
      !MIGRATION_MODAL_EXCLUDED_ACCOUNT_IDS.has(session?.account?.id as number),
    [session]
  );

  const handleSwitchShowNotifications = useCallback(
    () =>
      setShowNotifications((prevShowNotifications) => !prevShowNotifications),
    []
  );

  if (!session) {
    return (
      <div className="h-full w-full flex justify-center items-center">
        <Loading />
      </div>
    );
  }

  if (
    !session.account &&
    !["welcome", "account-selection"].includes(routeName as string)
  ) {
    if (invitations && invitations.length > 0) {
      return <Navigate to={buildRoute("account-selection")} />;
    } else {
      return <NoInvitationsPage />;
    }
  }

  if (routeName !== "welcome") {
    return (
      <div className="flex h-screen w-screen">
        <TermsAndConditionsAgreement show={!session.accepted_terms} />
        {showMigrationModal && (
          <MigrationSchedulingModal
            open={isMigrationModalOpen}
            onOpenChange={handleMigrationModalOpenChange}
          />
        )}

        <AppSidebar onSwitchShowNotifications={handleSwitchShowNotifications} />
        <div className="flex flex-col flex-1 h-full w-full overflow-hidden">
          <MobileMenu
            onSwitchShowNotifications={handleSwitchShowNotifications}
          />
          <NetworkWarning />
          <NotificationSlideOver
            show={showNotifications}
            onClose={handleSwitchShowNotifications}
          >
            <UserNotificationList onClose={handleSwitchShowNotifications} />
          </NotificationSlideOver>
          <main className="flex-1 min-h-0 h-full w-full overflow-y-auto">
            {children}
          </main>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export default PrivateRoute;
